/**
 * ============================================================================
 * FAMILY MEMBER CARD v1.0.0 - Einzelne Familienmitglied-Karte
 * ============================================================================
 *
 * Zeigt ein einzelnes Familienmitglied in der Liste an:
 * - Avatar mit Initialen
 * - Name und Rolle
 * - Arbeitsort und Zeitplan
 * - Alter
 * - Verfügbarkeitsstatus (🔴/🟢)
 *
 * @version 1.0.0
 * @date 2026-01-16
 * ============================================================================
 */

import React from 'react';
import { useTheme } from '../../context/ThemeContext';

console.log('👤 FamilyMemberCard.js v1.0.0 loaded');

/**
 * Berechnet ob die Person gerade verfügbar ist basierend auf dem Zeitplan
 * @param {string} schedule - z.B. "09:00-17:30"
 * @returns {boolean} - true wenn außerhalb der Arbeitszeit
 */
function isAvailable(schedule) {
  if (!schedule) return true;

  const match = schedule.match(/(\d{2}):(\d{2})-(\d{2}):(\d{2})/);
  if (!match) return true;

  const [, startH, startM, endH, endM] = match.map(Number);
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;

  // Verfügbar wenn AUSSERHALB der Arbeitszeit
  return currentMinutes < startMinutes || currentMinutes > endMinutes;
}

/**
 * Generiert Initialen aus dem Namen
 * @param {string} name
 * @returns {string} - z.B. "TM" für "Thomas Müller"
 */
function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * FamilyMemberCard Component
 */
function FamilyMemberCard({
  member,
  isSelected,
  onClick,
  compact = false
}) {
  const { isDark } = useTheme();
  const styles = getStyles(isDark, isSelected, member.color);

  const available = isAvailable(member.schedule);

  return (
    <div
      style={styles.card}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    >
      {/* Status Indicator */}
      <div style={styles.statusDot(available)} title={available ? 'Verfügbar' : 'Beschäftigt'} />

      {/* Avatar */}
      <div style={styles.avatar}>
        {member.avatar || getInitials(member.name)}
      </div>

      {/* Info */}
      <div style={styles.info}>
        <div style={styles.nameRow}>
          <span style={styles.name}>{member.name}</span>
          {member.role && (
            <span style={styles.roleTag}>({member.role})</span>
          )}
        </div>

        {!compact && (
          <>
            {member.location && (
              <div style={styles.location}>
                📍 {member.location}
                {member.schedule && ` (${member.schedule})`}
              </div>
            )}

            {member.age && (
              <div style={styles.age}>
                {member.age} Jahre
              </div>
            )}
          </>
        )}
      </div>

      {/* Arrow indicator when selected */}
      {isSelected && (
        <div style={styles.selectedIndicator}>▶</div>
      )}
    </div>
  );
}

/**
 * Styles
 */
function getStyles(isDark, isSelected, memberColor) {
  const colors = isDark ? {
    bg: isSelected ? '#374151' : '#1F2937',
    bgHover: '#374151',
    text: '#F9FAFB',
    textSecondary: '#D1D5DB',
    textMuted: '#9CA3AF',
    border: isSelected ? '#4A90A4' : '#4B5563',
    accent: '#4A90A4'
  } : {
    bg: isSelected ? '#E8F4F8' : '#FFFFFF',
    bgHover: '#E8F4F8',
    text: '#1F2937',
    textSecondary: '#374151',
    textMuted: '#6B7280',
    border: isSelected ? '#4A90A4' : '#B8D4DC',
    accent: '#4A90A4'
  };

  const avatarColor = memberColor || '#4A90A4';

  return {
    card: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 16px',
      background: colors.bg,
      border: `2px solid ${colors.border}`,
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative',
      ':hover': {
        background: colors.bgHover,
        borderColor: colors.accent
      }
    },

    statusDot: (available) => ({
      position: 'absolute',
      top: '12px',
      left: '12px',
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      background: available ? '#22C55E' : '#EF4444',
      border: `2px solid ${isDark ? '#1F2937' : '#FFFFFF'}`,
      boxShadow: `0 0 0 2px ${available ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
    }),

    avatar: {
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      background: `linear-gradient(135deg, ${avatarColor}, ${adjustColor(avatarColor, 20)})`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.1rem',
      fontWeight: 700,
      color: 'white',
      flexShrink: 0,
      marginLeft: '8px'
    },

    info: {
      flex: 1,
      minWidth: 0
    },

    nameRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      flexWrap: 'wrap'
    },

    name: {
      fontWeight: 600,
      fontSize: '1rem',
      color: colors.text
    },

    roleTag: {
      fontSize: '0.85rem',
      color: colors.textMuted
    },

    location: {
      fontSize: '0.85rem',
      color: colors.textSecondary,
      marginTop: '4px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },

    age: {
      fontSize: '0.8rem',
      color: colors.textMuted,
      marginTop: '2px'
    },

    selectedIndicator: {
      color: colors.accent,
      fontSize: '0.9rem',
      marginLeft: 'auto'
    }
  };
}

/**
 * Helper: Adjusts color brightness
 */
function adjustColor(hex, percent) {
  if (!hex) return '#6BA893';

  // Remove # if present
  hex = hex.replace('#', '');

  // Parse RGB
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Adjust
  r = Math.min(255, Math.max(0, r + (r * percent / 100)));
  g = Math.min(255, Math.max(0, g + (g * percent / 100)));
  b = Math.min(255, Math.max(0, b + (b * percent / 100)));

  // Convert back
  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
}

export default FamilyMemberCard;
